import { Combobox, RoadSign, type ComboboxOption } from '@vlm/ui';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TRACKING_SIGN } from '../components/status';
import { useT } from '../i18n';
import { useTrackingOverview } from '../lib/live';

/** "Search the fleet": any vehicle by name, plate, device or IMEI, straight to it on the live map. "/" opens it. */
export function FleetSearch() {
  const { t } = useT();
  const navigate = useNavigate();
  const overview = useTrackingOverview();
  const trigger = useRef<HTMLButtonElement>(null);

  const options = useMemo<ComboboxOption<number>[]>(
    () =>
      (overview.data?.vehicles ?? []).map((v) => ({
        value: v.vehicleId,
        label: v.vehicleName,
        detail: v.plateNumber,
        keywords: `${v.deviceName ?? ''} ${v.imei ?? ''}`,
        icon: <RoadSign sign={TRACKING_SIGN[v.status]} size={18} />,
      })),
    [overview.data],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      trigger.current?.click();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="w-full max-w-[340px] min-w-0">
      <Combobox
        triggerRef={trigger}
        ariaLabel={t('live.search')}
        value={null}
        onChange={(id) => id !== null && navigate(`/live?vehicle=${id}`)}
        options={options}
        loading={overview.isLoading}
        placeholder={t('live.search')}
        searchPlaceholder={t('live.search')}
        emptyText={t('live.noMatch')}
        leading={<Search size={16} aria-hidden="true" className="shrink-0 text-ink-subtle" />}
        trailing={<kbd className="hidden rounded-xs border border-line-strong px-[5px] py-[3px] font-mono text-[11px] font-semibold leading-none text-ink-subtle sm:block">/</kbd>}
        className="h-[34px] bg-surface-sunken"
      />
    </div>
  );
}
